import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "../components/ui/dialog";
import { Loader2, Send, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUBJECT_OPTIONS = [
    "Compraventa de inmuebles",
    "Testamentos",
    "Poderes notariales",
    "Constitución de sociedades",
    "Actas y fe de hechos",
    "Donaciones",
    "Otra consulta",
];

export default function ContactModal({ open, onOpenChange }) {
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: SUBJECT_OPTIONS[0],
        message: "",
    });

    const update = (key) => (e) => setForm((s) => ({ ...s, [key]: e.target.value }));

    // Formato visual del teléfono: "614 123 4567"
    const formatPhone = (digits) => {
        const d = digits.slice(0, 10);
        if (d.length <= 3) return d;
        if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`;
        return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
    };

    const handlePhoneChange = (e) => {
        const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 10);
        setForm((s) => ({ ...s, phone: onlyDigits }));
    };

    // Mensajes nativos del navegador (HTML5) en español
    const onInvalidMsg = (msg) => (e) => {
        e.target.setCustomValidity(msg);
    };
    const clearCustomValidity = (e) => {
        e.target.setCustomValidity("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;

        const name = form.name.trim();
        const email = form.email.trim();
        const message = form.message.trim();
        const subject = form.subject.trim();

        if (!name || !email || !message) {
            toast.error("Por favor completa nombre, correo y mensaje.");
            return;
        }
        if (name.length < 2) {
            toast.error("El nombre debe tener al menos 2 caracteres.");
            return;
        }
        if (name.length > 120) {
            toast.error("El nombre es demasiado largo (máx. 120 caracteres).");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            toast.error("Ingresa un correo electrónico válido.");
            return;
        }
        if (!subject || subject.length < 2) {
            toast.error("Selecciona un asunto.");
            return;
        }
        if (message.length < 5) {
            toast.error("El mensaje debe tener al menos 5 caracteres.");
            return;
        }
        if (message.length > 4000) {
            toast.error("El mensaje es demasiado largo (máx. 4000 caracteres).");
            return;
        }
        if (form.phone && form.phone.length !== 10) {
            toast.error("El teléfono debe tener 10 dígitos.");
            return;
        }
        try {
            setSubmitting(true);
            const payload = {
                name,
                email,
                subject,
                message,
                phone: form.phone ? `+52 ${formatPhone(form.phone)}` : "",
            };
            const res = await axios.post(`${API}/contact`, payload);
            if (res.data?.status === "success") {
                toast.success("Mensaje enviado. Te contactaremos pronto.");
                setForm({
                    name: "",
                    email: "",
                    phone: "",
                    subject: SUBJECT_OPTIONS[0],
                    message: "",
                });
                onOpenChange(false);
            } else {
                toast.error("No se pudo enviar el mensaje. Intenta de nuevo.");
            }
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail || "Error al enviar el mensaje.";
            if (status === 429) {
                toast.error(detail, { duration: 6000 });
            } else {
                toast.error(detail);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="contact-modal"
                className="sm:max-w-[640px] bg-[#08111D] border border-[#D4AF37]/25 rounded-none p-0 overflow-hidden text-[#F3F4F6] [&>button]:hidden"
            >
                <button
                    type="button"
                    onClick={() => onOpenChange(false)}
                    data-testid="contact-modal-close"
                    className="absolute right-4 top-4 z-10 w-9 h-9 flex items-center justify-center border border-[#D4AF37]/25 hover:border-[#D4AF37] text-[#D4AF37]/70 hover:text-[#D4AF37] transition-all"
                    aria-label="Cerrar"
                >
                    <X size={16} />
                </button>

                <div className="px-8 pt-10 pb-6 border-b border-[#D4AF37]/15 bg-gradient-to-b from-[#0A1628] to-[#08111D]">
                    <DialogHeader>
                        <p className="overline">Contacto Directo</p>
                        <DialogTitle className="font-serif-display text-3xl md:text-4xl font-light text-[#F3F4F6] mt-3 leading-tight">
                            Agende su consulta
                        </DialogTitle>
                        <DialogDescription className="text-[#9CA3AF] text-sm mt-3 leading-relaxed">
                            Notaría 25 · Guadalupe, Chihuahua. Le responderemos a la brevedad
                            con la atención profesional que su asunto requiere.
                        </DialogDescription>
                    </DialogHeader>
                </div>

                <form onSubmit={handleSubmit} className="px-8 py-8 space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                            <label className="form-label">Nombre completo</label>
                            <input
                                data-testid="contact-input-name"
                                type="text"
                                required
                                minLength={2}
                                maxLength={120}
                                value={form.name}
                                onChange={update("name")}
                                onInvalid={onInvalidMsg(
                                    "Por favor ingresa tu nombre (mínimo 2 caracteres)."
                                )}
                                onInput={clearCustomValidity}
                                placeholder="Ej. Andrea Mayoral"
                                className="form-input"
                            />
                        </div>
                        <div>
                            <label className="form-label">Teléfono</label>
                            <div className="flex">
                                <div className="flex items-center gap-2 px-3 bg-[rgba(10,22,40,0.6)] border border-r-0 border-[#D4AF37]/20 text-[#F3F4F6] text-[0.95rem] select-none">
                                    <svg
                                        width="22"
                                        height="16"
                                        viewBox="0 0 6 4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        aria-label="México"
                                    >
                                        <rect width="2" height="4" x="0" fill="#006847" />
                                        <rect width="2" height="4" x="2" fill="#FFFFFF" />
                                        <rect width="2" height="4" x="4" fill="#CE1126" />
                                        <circle cx="3" cy="2" r="0.45" fill="#A0522D" />
                                    </svg>
                                    <span className="text-[#9CA3AF]">+52</span>
                                </div>
                                <input
                                    data-testid="contact-input-phone"
                                    type="tel"
                                    inputMode="numeric"
                                    autoComplete="tel-national"
                                    maxLength={12}
                                    value={formatPhone(form.phone)}
                                    onChange={handlePhoneChange}
                                    placeholder="614 123 4567"
                                    className="form-input flex-1"
                                />
                            </div>
                            <p className="mt-1.5 text-[10px] tracking-[0.18em] uppercase text-[#9CA3AF]/60">
                                10 dígitos · Opcional
                            </p>
                        </div>
                    </div>

                    <div>
                        <label className="form-label">Correo electrónico</label>
                        <input
                            data-testid="contact-input-email"
                            type="email"
                            required
                            value={form.email}
                            onChange={update("email")}
                            onInvalid={onInvalidMsg(
                                "Ingresa un correo electrónico válido (ej. usted@correo.com)."
                            )}
                            onInput={clearCustomValidity}
                            placeholder="usted@correo.com"
                            className="form-input"
                        />
                    </div>

                    <div>
                        <label className="form-label">Asunto</label>
                        <select
                            data-testid="contact-input-subject"
                            value={form.subject}
                            onChange={update("subject")}
                            className="form-input appearance-none cursor-pointer"
                        >
                            {SUBJECT_OPTIONS.map((opt) => (
                                <option key={opt} value={opt} className="bg-[#0A1628]">
                                    {opt}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <div className="flex items-center justify-between">
                            <label className="form-label">Mensaje</label>
                            <span
                                className={`text-[10px] tracking-[0.18em] uppercase ${
                                    form.message.trim().length < 5
                                        ? "text-[#9CA3AF]/60"
                                        : "text-[#D4AF37]/80"
                                }`}
                            >
                                {form.message.trim().length}/4000 · mín. 5
                            </span>
                        </div>
                        <textarea
                            data-testid="contact-input-message"
                            required
                            minLength={5}
                            maxLength={4000}
                            rows={4}
                            value={form.message}
                            onChange={update("message")}
                            onInvalid={onInvalidMsg(
                                "Por favor escribe un mensaje (mínimo 5 caracteres)."
                            )}
                            onInput={clearCustomValidity}
                            placeholder="Cuéntenos brevemente cómo podemos ayudarle…"
                            className="form-input resize-none"
                        />
                    </div>

                    <div className="flex items-center justify-between gap-4 pt-4 border-t border-[#D4AF37]/10">
                        <p className="text-xs text-[#9CA3AF]/80 max-w-[260px] leading-relaxed">
                            Sus datos son tratados con absoluta confidencialidad notarial.
                        </p>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="contact-submit-button"
                            className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Enviando
                                </>
                            ) : (
                                <>
                                    Enviar mensaje
                                    <Send size={14} />
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
